import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { http, saveSessionToken, onUnauthorized, getSessionToken } from "@/api";
import { setAuthDiag } from "@/lib/diagnostics";
import { toast } from "sonner";

// AuthProvider owns the "am I logged in?" state for the whole app. Three tri-state values on
// `user`: undefined = still checking, null = definitely logged out, object = logged in.
// The server is the source of truth: /auth/me is probed on mount, whenever the app comes back
// to the foreground (a PWA can sleep for days — long enough for the 7-day session to lapse),
// and whenever any data request answers 401.
// See /app/auth_testing.md for the E2E playbook.
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const REPROBE_MIN_MS = 60_000;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(undefined);
    const lastProbeRef = useRef(0);
    const hadUserRef = useRef(false);

    const refresh = useCallback(async () => {
        lastProbeRef.current = Date.now();
        try {
            const r = await http.get("/auth/me");
            hadUserRef.current = true;
            setUser(r.data);
            setAuthDiag({ authenticated: true, source: getSessionToken() ? "bearer+cookie" : "cookie", lastVerify: "ok", lastVerifyAt: new Date().toISOString() });
            return true;
        } catch (err) {
            setAuthDiag({ lastVerify: `failed ${err?.response?.status || "network"}`, lastVerifyAt: new Date().toISOString() });
            if (err?.response?.status === 401) {
                setAuthDiag({ authenticated: false });
                saveSessionToken(null); // the server rejected it — a dead token must not keep shadowing
                if (hadUserRef.current) toast.error("Session expired — please sign in again", { id: "session-expired", duration: 6000 });
                hadUserRef.current = false;
                setUser(null);
            } else if (!hadUserRef.current) {
                setUser(null); // network failure on first load → login screen (never a blank dashboard)
            }
            return false;
        }
    }, []);

    useEffect(() => {
        // CRITICAL — if the URL hash contains `session_id=…`, the OAuth callback handler is about
        // to swap it for a real session. Skip the /auth/me probe in that window so we don't race
        // into a false "logged out" state that redirects away from the callback.
        if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
            setUser(null);
            return;
        }
        refresh();
    }, [refresh]);

    useEffect(() => {
        onUnauthorized(() => { refresh(); });
        const onVisible = () => {
            if (document.visibilityState !== "visible" || !hadUserRef.current) return;
            if (Date.now() - lastProbeRef.current < REPROBE_MIN_MS) return;
            refresh();
        };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onVisible);
        return () => {
            onUnauthorized(null);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", onVisible);
        };
    }, [refresh]);

    const logout = useCallback(async () => {
        try { await http.post("/auth/logout"); } catch { /* ignore */ }
        saveSessionToken(null);
        hadUserRef.current = false;
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, setUser, refresh, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
